import { useCallback, useEffect, useMemo } from 'react';
import { useTenants } from '../tenants/useTenants';
import { useUserNames } from '../../Helpers/useUserNames';
import { KeyHolder, RegisterKey } from '../../types/keyRegister';

export interface UseKeyHolderNamesReturn {
    /** Tenant name from the tenants root; app and external users from `users/userList/{uid}/name`. */
    holderName: (holder: KeyHolder | undefined) => string;
    actorName: (uid: string) => string;
}

/**
 * Names for the holders and actors of the given keys. Tenants come from the
 * module-cached `useTenants`; app users and external users both live in
 * `users/userList` (external ones with `isExternal`), so one `useUserNames`
 * lookup covers both.
 */
export const useKeyHolderNames = (keys: Array<Pick<RegisterKey, 'holder' | 'log'>>): UseKeyHolderNamesReturn => {
    const { tenantsByKey } = useTenants();
    const { userNames, loadUserNames } = useUserNames();

    const uids = useMemo(() => {
        const set = new Set<string>();
        const addHolder = (holder: KeyHolder | undefined) => {
            if (holder && holder.kind !== 'tenant') set.add(holder.key);
        };
        for (const key of keys) {
            addHolder(key.holder);
            for (const event of key.log) {
                if (event.actor) set.add(event.actor);
                addHolder(event.holder);
            }
        }
        return Array.from(set).sort();
    }, [keys]);
    const uidsSignature = uids.join(',');

    useEffect(() => {
        if (uids.length > 0) void loadUserNames(uids);
        // uidsSignature stands for uids; loadUserNames skips cached ones.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uidsSignature, loadUserNames]);

    const userName = useCallback((uid: string, fallback: string): string => {
        const name = userNames[uid];
        if (name === undefined) return '…';
        return name || fallback;
    }, [userNames]);

    const holderName = useCallback((holder: KeyHolder | undefined): string => {
        if (!holder) return '–';
        if (holder.kind === 'tenant') return tenantsByKey[holder.key]?.name ?? 'Okänd person';
        return userName(holder.key, holder.kind === 'external' ? 'Okänd extern användare' : 'Okänd användare');
    }, [tenantsByKey, userName]);

    const actorName = useCallback((uid: string): string => userName(uid, 'Okänd användare'), [userName]);

    return { holderName, actorName };
};
